import React, { useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Button
} from '@mui/material';
import { KeyboardArrowDown, ViewQuilt } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useLoading } from 'contexts/LoadingContext';
interface Option {
  id: string;
  label: string;
}
interface Props {
  options: Option[];
  defaultSelectedId?: string;
  onChange: (option: Option) => void;
  buttonText?: string;
}
const Dropdown = ({ 
  options,
  defaultSelectedId = 'vtour-vtour',
  onChange,
  buttonText = 'View Mode'
}: Props) => {
  const { fullscreen } = useLoading();
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    options.find(option => option.id === defaultSelectedId) || options[0]
  );
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [fullscreenElement, setFullscreenElement] = useState<Element | null>(null);

  useEffect(() => {
    setFullscreenElement(document.fullscreenElement);
  }, [fullscreen]);

  useEffect(() => {
    const newSelectedOption = options.find(option => option.id === defaultSelectedId);
    if (newSelectedOption) {
      setSelectedOption(newSelectedOption);
    }
  }, [defaultSelectedId, options]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (option: Option) => {
    console.log("menu items", option);
    setSelectedOption(option);
    onChange(option);
    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  React.useEffect(() => {
    console.log("Dropdown state:", { open, anchorRef: !!anchorRef.current });
  }, [open]);

  return (
    <Stack direction="row" alignItems="center">
      <Button
        ref={anchorRef}
        id="dropdown-button"
        aria-controls={open ? 'dropdown-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        endIcon={<KeyboardArrowDown />}
        startIcon={<ViewQuilt />}
        sx={(theme) => ({
          backgroundColor: alpha(theme.palette.common.white, 0.05),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
          color: theme.palette.common.white,
          borderRadius: 3,
          position: 'relative',
          px: 2,
          py: 1,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.12),
          },
          transition: 'all 0.2s ease-in-out',
        })}
      >
        <Typography variant="body2" fontWeight="medium">{buttonText}</Typography>
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal={false}
        container={fullscreenElement || document.body}
        placement="bottom-start"
        sx={{ 
          zIndex: 1300,
          width: 240
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: 'top left' }}
          >
            <Paper
              sx={{
                backgroundColor: alpha('#121212', 0.85),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha('#ffffff', 0.12)}`,
                borderRadius: 2,
                mt: 1,
                overflow: 'hidden',
                boxShadow: `0 8px 32px 0 ${alpha('#000000', 0.37)}`
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="glass-menu-list-grow"
                  onKeyDown={handleListKeyDown}
                  sx={{ py: 0.5 }}
                >
                  {options.map((option) => (
                    <MenuItem 
                      key={option.id}
                      selected={option.id === selectedOption.id}
                      onClick={() => handleMenuItemClick(option)}
                      sx={{
                        color: '#ffffff',
                        py: 1.5,
                        px: 2,
                        borderLeft: option.id === selectedOption.id ? 
                          '3px solid #3f8cff' : '3px solid transparent',
                        '&:hover': {
                          backgroundColor: alpha('#ffffff', 0.07),
                        },
                        '&.Mui-selected': {
                          backgroundColor: alpha('#3f8cff', 0.15),
                          '&:hover': {
                            backgroundColor: alpha('#3f8cff', 0.25),
                          }
                        }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: option.id === selectedOption.id ? 'medium' : 'regular',
                          opacity: option.id === selectedOption.id ? 1 : 0.85
                        }}
                      >
                        {option.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Stack>
  );
};

export default Dropdown;